import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Edit3, ChevronLeft, ChevronRight, BookOpen,
  FileText, Code, CheckSquare, Eye, Maximize2, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';
import MarkdownRenderer from './common/MarkdownRenderer';
import { Breadcrumbs } from './ui';

function conceptStatusDotClass(status) {
  if (status === 'complete') return 'bg-status-good';
  if (status === 'draft') return 'bg-status-warn';
  return 'bg-mastery-track';
}


export default function DomainContentViewer() {
  const { courseId, domain } = useParams();
  const [course, setCourse] = useState(null);
  const [domainContent, setDomainContent] = useState({});
  const [currentConceptIndex, setCurrentConceptIndex] = useState(0);
  const [contentType, setContentType] = useState('material');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  const contentTypes = [
    { key: 'material', label: 'Material', icon: BookOpen },
    { key: 'examples', label: 'Examples', icon: Code },
    { key: 'exercises', label: 'Exercises', icon: CheckSquare },
    { key: 'guide', label: 'Guide', icon: FileText }
  ];

  useEffect(() => {
    loadData();
  }, [courseId, domain, contentType]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') prevConcept();
      if (e.key === 'ArrowRight') nextConcept();
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setFocusMode(!focusMode);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentConceptIndex, focusMode]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const courseResponse = await apiService.getCourse(courseId);
      setCourse(courseResponse.data);

      const contentResponse = await apiService.getDomainContent(courseId, domain, contentType);
      setDomainContent(contentResponse.data);
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const getDomainConfig = () => {
    return course?.domains?.find(d => d.key === domain);
  };

  const getConcepts = () => {
    return getDomainConfig()?.concepts || [];
  };

  const getCurrentConcept = () => {
    const concepts = getConcepts();
    return concepts[currentConceptIndex];
  };

  const getCurrentContent = () => {
    const concept = getCurrentConcept();
    return domainContent[concept] || { content: '', status: 'empty' };
  };

  const nextConcept = () => {
    const concepts = getConcepts();
    if (currentConceptIndex < concepts.length - 1) {
      setCurrentConceptIndex(currentConceptIndex + 1);
    }
  };

  const prevConcept = () => {
    if (currentConceptIndex > 0) {
      setCurrentConceptIndex(currentConceptIndex - 1);
    }
  };

  const getConceptStatus = (concept) => {
    return domainContent[concept]?.status || 'empty';
  };

  if (loading) return <Loading message="Loading domain content..." />;

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <h2 className="text-xl font-semibold text-status-bad mb-4">Failed to load content</h2>
        <p className="text-mutedink mb-6">{error}</p>
        <button onClick={loadData} className="btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  const domainConfig = getDomainConfig();
  const concepts = getConcepts();
  const currentConcept = getCurrentConcept();
  const currentContent = getCurrentContent();
  const progressPercentage = ((currentConceptIndex + 1) / concepts.length) * 100;

  return (
    <div className="min-h-screen bg-paper">
      {/* Ultra Minimal Header */}
      {!focusMode && (
        <div className="border-b border-line bg-surface/80 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-[1600px] mx-auto px-12 py-5">
            <div className="flex items-center justify-between gap-6">
              <Breadcrumbs items={[
                { label: 'My Courses', to: '/my-courses' },
                { label: course?.name, to: `/course/${courseId}` },
                { label: domainConfig?.name || domain },
              ]} />

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFocusMode(true)}
                  className="p-2 text-mutedink hover:text-ink hover:bg-surface-subtle rounded-lg transition-all duration-200"
                  title="Focus Mode (Ctrl/Cmd + F)"
                >
                  <Maximize2 size={18} />
                </button>
                <Link
                  to={`/course/${courseId}/manage/${encodeURIComponent(domain)}/${encodeURIComponent(currentConcept)}`}
                  className="px-4 py-2 text-sm font-medium text-read hover:text-ink hover:bg-surface-subtle rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <Edit3 size={16} />
                  Edit
                </Link>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="h-0.5 bg-surface-subtle">
            <div 
              className="h-full bg-accent transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-12 py-10">
        <div className="flex gap-10">
          
          {/* Collapsible Sidebar */}
          {!focusMode && (
            <div 
              className={`transition-all duration-300 ease-in-out ${
                sidebarCollapsed ? 'w-14' : 'w-72'
              } flex-shrink-0`}
            >
              <div className="sticky top-28">
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="mb-6 p-2 hover:bg-surface-subtle rounded-lg transition-colors"
                >
                  {sidebarCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
                </button>

                {!sidebarCollapsed && (
                  <div className="space-y-8 animate-fadeIn">
                    {/* Content Type Pills */}
                    <div>
                      <h3 className="text-xs font-semibold text-mutedink uppercase tracking-wider mb-4">
                        Type
                      </h3>
                      <div className="space-y-1.5">
                        {contentTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.key}
                              onClick={() => setContentType(type.key)}
                              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-3 group ${
                                contentType === type.key
                                  ? 'bg-ink text-paper'
                                  : 'text-mutedink hover:bg-surface-subtle hover:text-ink'
                              }`}
                            >
                              <Icon size={18} className="transition-transform group-hover:scale-110" />
                              <span className="font-medium text-sm">{type.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Concept Navigation */}
                    <div>
                      <h3 className="text-xs font-semibold text-mutedink uppercase tracking-wider mb-4">
                        Concepts
                      </h3>
                      <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin">
                        {concepts.map((concept, index) => {
                          const status = getConceptStatus(concept);
                          return (
                            <button
                              key={concept}
                              onClick={() => setCurrentConceptIndex(index)}
                              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 group ${
                                index === currentConceptIndex
                                  ? 'bg-surface-subtle text-ink shadow-sm'
                                  : 'text-mutedink hover:bg-surface-muted hover:text-ink'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="font-medium text-sm truncate">
                                  {concept.replace(/_/g, ' ')}
                                </span>
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-all ${conceptStatusDotClass(status)}`} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Expansive Content Area */}
          <div className="flex-1 min-w-0">
            
            {/* Floating Navigation */}
            {!focusMode && (
              <div className="flex items-center justify-between mb-10 pb-8 border-b border-line">
                <div className="flex-1">
                  <h2 className="text-3xl font-semibold text-ink mb-2 tracking-tight">
                    {currentConcept?.replace(/_/g, ' ') || 'Select a Concept'}
                  </h2>
                  <p className="text-sm text-mutedink font-medium">
                    {contentTypes.find(t => t.key === contentType)?.label}
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="px-4 py-2 bg-surface-subtle rounded-full">
                    <span className="text-sm font-medium text-read">
                      {currentConceptIndex + 1} <span className="text-faint">/</span> {concepts.length}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevConcept}
                      disabled={currentConceptIndex === 0}
                      className="p-2.5 rounded-xl hover:bg-surface-subtle disabled:opacity-20 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95"
                    >
                      <ChevronLeft size={22} className="text-mutedink" />
                    </button>
                    
                    <button
                      onClick={nextConcept}
                      disabled={currentConceptIndex === concepts.length - 1}
                      className="p-2.5 rounded-xl hover:bg-surface-subtle disabled:opacity-20 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95"
                    >
                      <ChevronRight size={22} className="text-mutedink" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Focus Mode Header */}
            {focusMode && (
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-line">
                <h2 className="text-2xl font-semibold text-ink">
                  {currentConcept?.replace(/_/g, ' ')}
                </h2>
                <button
                  onClick={() => setFocusMode(false)}
                  className="px-3 py-1.5 text-sm text-mutedink hover:text-ink hover:bg-surface-subtle rounded-lg transition-colors"
                >
                  Exit Focus
                </button>
              </div>
            )}

            {/* Premium Content Display */}
            <div className={`min-h-[700px] ${focusMode ? 'max-w-4xl mx-auto' : ''}`}>
              {currentContent.content ? (
                <article className="prose prose-lg prose-gray max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-h1:text-4xl prose-h1:mb-6 prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-read prose-p:leading-relaxed prose-p:mb-6 prose-a:text-ink prose-a:font-medium hover:prose-a:text-mutedink prose-code:text-ink prose-code:bg-surface-subtle prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-[var(--code-block-bg)] prose-pre:shadow-2xl">
                  <MarkdownRenderer content={currentContent.content} />
                </article>
              ) : (
                <div className="text-center py-40">
                  <div className="w-20 h-20 bg-surface-subtle border border-line rounded-3xl flex items-center justify-center mx-auto mb-8">
                    <Eye className="w-10 h-10 text-faint" />
                  </div>
                  <h3 className="text-2xl font-semibold text-ink mb-3 tracking-tight">No Content Yet</h3>
                  <p className="text-mutedink mb-10 text-base max-w-md mx-auto">
                    This concept doesn't have any {contentTypes.find(t => t.key === contentType)?.label.toLowerCase()} yet.
                  </p>
                  <Link
                    to={`/course/${courseId}/manage/${encodeURIComponent(domain)}/${encodeURIComponent(currentConcept)}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-[var(--on-accent)] rounded-xl hover:bg-accent-hover transition-all duration-200 text-sm font-medium"
                  >
                    <Edit3 size={18} />
                    Create Content
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Elegant Footer Hint */}
        {!focusMode && (
          <div className="mt-16 pt-8 border-t border-line flex justify-center gap-8 text-xs text-faint">
            <span>← → Navigate concepts</span>
            <span>•</span>
            <span>⌘/Ctrl + F Focus mode</span>
          </div>
        )}
      </div>
    </div>
  );
}